// Start a QC check on a delivery, or on one pallet of it (mig 20261296000000):
// the template list for the delivery's fruit, then the same capture screen as
// a run's New Check, saved against the delivery (and the pallet). Shared by the
// delivery's QC card, each pallet's QC button and the QC "To check" queue.
//
// The capture screen (QC.tsx's RunQcTemplate) is passed in as Runner: a page
// outside QC passes LazyRunQcTemplate, so it does not pull the QC module in,
// and QC passes its own, so QC never imports itself back through this file.

import { Suspense, useState, type ComponentType } from 'react'
import Modal from '@/components/Modal'
import QueryStateGate from '@/components/QueryStateGate'
import { useQcPickableTemplates, type QcPickableTemplate } from '@/hooks/useQcPickableTemplates'
import { templatesForRun } from '@/lib/qcCustomChecks'
import { deliveryQcSubject, palletCheckLabel, type QcDeliveryRef, type QcPalletRef, type QcSubject } from '@/lib/qcSubject'

/** The capture screen's shape (RunQcTemplate). */
export type QcRunner = ComponentType<{ template: QcPickableTemplate; subject: QcSubject; onClose: () => void; onSaved: () => void }>

export type QcDelivery = QcDeliveryRef & { delivery_id: string; variety: string | null }

/** Templates offered for a delivery: the run rules with the delivery's
 *  variety, minus the run-only system lanes (titration, packer problems). */
export function templatesForDelivery<T extends QcPickableTemplate>(templates: T[], variety: string | null): T[] {
  return templatesForRun(templates, { runVariety: variety ?? '', runCommodityId: null, hasTitrationQc: false, hasPackerQc: false })
}

/** The template picker, then the capture screen. `template` skips the picker
 *  (the intake form's pick). Closing either step calls onClose. */
export function DeliveryQcStart({ Runner, delivery, pallet = null, template = null, onClose, onSaved }: {
  Runner:   QcRunner
  delivery: QcDelivery
  pallet?:  QcPalletRef | null
  template?: QcPickableTemplate | null
  onClose:  () => void
  onSaved:  () => void
}) {
  const [running, setRunning] = useState<QcPickableTemplate | null>(template)
  const templatesQ = useQcPickableTemplates({ enabled: !template })
  const offered = templatesForDelivery(templatesQ.data ?? [], delivery.variety)
  const subject = deliveryQcSubject(delivery, null, pallet)

  if (running) {
    return (
      <Suspense fallback={null}>
        <Runner template={running} subject={subject} onClose={onClose} onSaved={onSaved} />
      </Suspense>
    )
  }
  return (
    <Modal title={pallet ? `QC check on ${palletCheckLabel(pallet).toLowerCase()}` : 'QC check on this delivery'}
      onClose={onClose} size="md" noFooter>
      <div className="space-y-2">
        <p className="text-caption text-fg-muted truncate">Pick a template · {subject.label} · {delivery.variety ?? '—'}</p>
        {offered.length === 0 ? (
          <QueryStateGate query={templatesQ} noun="QC templates" empty={
            <div className="card p-6 text-center text-sm text-fg-muted">
              No templates apply to this fruit. An admin can add one under QC → Set-up → Templates.
            </div>
          } />
        ) : (
          <ul className="space-y-2">
            {offered.map(t => (
              <li key={t.template_id}>
                <button type="button" onClick={() => setRunning(t)}
                  className="card p-3 w-full min-h-[44px] text-left hover:bg-raised/50 flex items-center gap-2">
                  <span className="min-w-0 flex-1 font-semibold text-fg truncate">{t.name}</span>
                  <span className="text-fg-muted text-sm shrink-0">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  )
}
